import { isAudioWorkEnabled, type AudioWorkContext } from '../../audioWorkBarrier';
import { getSafeErrorFields } from '../../errors';
import { setLastChunkIndex, type RecordingKey } from '../../persistenceV2';
import { isMemoryOnlyWriteError } from '../../persistenceV2/writeFence';
import { logger } from '../lib/logger';
import { logStorePersistFailure } from '../lib/logStorePersistFailure';
import { simpleRetry } from '../simpleRetry';
import { maybeCompleteRecordingV2 } from './processChunkV2';
import { type GetState, type SetState } from './storeTypes';

// A throwing host is logged, never propagated into the kit's own delivery.
const notifyCaptureEnded = (get: GetState, key: RecordingKey, lastIndex: number) => {
  try {
    get().config?.onCaptureEnded?.(key.recordingId, {
      sessionId: key.sessionId,
      lastChunkIndex: lastIndex,
    });
  } catch (err) {
    logger.error({
      event_type: 'CAPTURE_ENDED_CALLBACK_FAILED',
      details: { ...key, ...getSafeErrorFields(err) },
    });
  }
};

/** Publishes the final index, then checks completion; empty captures only clean up. */
export const handleCaptureEndedV2 = async (
  set: SetState,
  get: GetState,
  key: RecordingKey,
  lastIndex: number,
  context?: AudioWorkContext
) => {
  // Before the boundary write, whose retries back off for seconds when storage
  // fails: the host's finalize clock starts at capture end. Not for an empty
  // capture, which delivers nothing, nor during an audio shutdown, whose
  // delivery never settles on this page.
  if (lastIndex >= 0 && isAudioWorkEnabled(context)) {
    notifyCaptureEnded(get, key, lastIndex);
  }

  try {
    context?.throwIfStale();
    await simpleRetry(() => setLastChunkIndex(key, lastIndex));
  } catch (err) {
    if (context && !context.isCurrent()) return;
    // A memory-only refusal is the start's storage-less event again at capture
    // end: expected, and loud enough at error level to bury the real faults.
    logStorePersistFailure(
      {
        event_type: 'SET_LAST_CHUNK_INDEX_ERROR',
        recording_id: key.recordingId,
        details: { error: err instanceof Error ? err.message : String(err) },
      },
      { memoryOnly: isMemoryOnlyWriteError(err) }
    );
  }

  if (lastIndex < 0) {
    void get().cleanupRecordingsTask();
    return;
  }

  await maybeCompleteRecordingV2(set, get, key, context);
};
